import React, { useState, useEffect } from 'react';
import BookFileSelector from './BookFileSelector';
import FileSyncStatusIcon from './FileSyncStatusIcon';
import BulkIsbnPasteModal from './BulkIsbnPasteModal';
import { settingsService } from '../services/SettingsService';
import { useAuth } from '../contexts/AuthContext';
import { FolderService } from '../services/FolderService';
import { authenticatedFetch } from '../utils/api';
import { parseIsbnsFromPaste } from '../utils/isbnPaste';

// Book selection state: count and DB-sync-eligible count (from BookTable)
type BookSelectionDetail = { count: number; dbSyncEligibleCount?: number };

type BulkProgress = { sent: number; received: number; total: number; done: boolean };

// Sync Selected Files Button Component
const SyncSelectedFilesButton: React.FC = () => {
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [bulkPasteOpen, setBulkPasteOpen] = useState(false);
  const [bulkPasteValue, setBulkPasteValue] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);

  useEffect(() => {
    const handleSelectionChange = (event: Event) => {
      const customEvent = event as CustomEvent<BookSelectionDetail>;
      setSelectedCount(customEvent.detail?.count || 0);
    };
    window.addEventListener('bookSelectionChanged', handleSelectionChange);
    return () => window.removeEventListener('bookSelectionChanged', handleSelectionChange);
  }, []);

  useEffect(() => {
    const handleProgress = (event: Event) => {
      const e = event as CustomEvent<BulkProgress>;
      setBulkProgress(e.detail ?? null);
      if (e.detail?.done) setBulkSending(false);
    };
    window.addEventListener('bulkUploadProgress', handleProgress);
    return () => window.removeEventListener('bulkUploadProgress', handleProgress);
  }, []);

  const handleClick = () => {
    if (selectedCount > 0) {
      window.dispatchEvent(new CustomEvent('syncSelectedBooks'));
    } else {
      setBulkPasteValue('');
      setBulkProgress(null);
      setBulkPasteOpen(true);
    }
  };

  const handleBulkSubmit = () => {
    const isbns = parseIsbnsFromPaste(bulkPasteValue);
    if (isbns.length === 0) return;
    setBulkSending(true);
    setBulkProgress({ sent: 0, received: 0, total: isbns.length, done: false });
    window.dispatchEvent(new CustomEvent('bulkUploadIsbns', { detail: { isbns } }));
  };

  const parsedCount = bulkPasteValue ? parseIsbnsFromPaste(bulkPasteValue).length : 0;

  return (
    <>
      <button
        onClick={handleClick}
        className="header-nav-button header-nav-button-icon-only"
        title={
          selectedCount > 0
            ? `Upload files for ${selectedCount} selected book${selectedCount > 1 ? 's' : ''}`
            : 'Upload files for selected books, or click to paste ISBNs in bulk'
        }
      >
        Upload Files ({selectedCount})
      </button>
      <BulkIsbnPasteModal
        open={bulkPasteOpen}
        onClose={() => setBulkPasteOpen(false)}
        title="Bulk upload by ISBN"
        description="Paste ISBNs below (one per line, or comma/space separated). Only 10- or 13-digit numbers are used."
        value={bulkPasteValue}
        onChange={setBulkPasteValue}
        parsedCount={parsedCount}
        onSubmit={handleBulkSubmit}
        submitButtonLabel="Queue for upload"
        submitting={bulkSending}
        progress={bulkProgress}
        canClose={!bulkSending}
      />
    </>
  );
};

// DB Sync Selected Button (syncs eligible selected books to admin tool QA + PROD). When 0 selected, opens bulk paste modal.
const DbSyncSelectedButton: React.FC = () => {
  const [dbSyncEligibleCount, setDbSyncEligibleCount] = useState<number>(0);
  const [dbSyncBulkOpen, setDbSyncBulkOpen] = useState(false);
  const [dbSyncBulkValue, setDbSyncBulkValue] = useState('');

  useEffect(() => {
    const handleSelectionChange = (event: Event) => {
      const customEvent = event as CustomEvent<BookSelectionDetail>;
      setDbSyncEligibleCount(customEvent.detail?.dbSyncEligibleCount ?? 0);
    };
    window.addEventListener('bookSelectionChanged', handleSelectionChange);
    return () => window.removeEventListener('bookSelectionChanged', handleSelectionChange);
  }, []);

  const handleClick = () => {
    if (dbSyncEligibleCount > 0) {
      window.dispatchEvent(new CustomEvent('dbSyncSelectedBooks'));
    } else {
      setDbSyncBulkValue('');
      setDbSyncBulkOpen(true);
    }
  };

  const handleDbSyncBulkSubmit = () => {
    const isbns = parseIsbnsFromPaste(dbSyncBulkValue);
    if (isbns.length === 0) return;
    window.dispatchEvent(new CustomEvent('dbSyncBulkIsbns', { detail: { isbns } }));
    setDbSyncBulkOpen(false);
  };

  const dbSyncParsedCount = dbSyncBulkValue ? parseIsbnsFromPaste(dbSyncBulkValue).length : 0;

  return (
    <>
      <button
        onClick={handleClick}
        className="header-nav-button header-nav-button-icon-only"
        title={
          dbSyncEligibleCount > 0
            ? `Sync DB for ${dbSyncEligibleCount} selected book${dbSyncEligibleCount > 1 ? 's' : ''} to admin tool (QA + PROD)`
            : 'No selection: paste ISBNs to bulk sync to DB, or select rows first'
        }
      >
        DB Sync ({dbSyncEligibleCount})
      </button>
      <BulkIsbnPasteModal
        open={dbSyncBulkOpen}
        onClose={() => setDbSyncBulkOpen(false)}
        title="Bulk DB sync by ISBN"
        description="Paste ISBNs below (one per line, or comma/space separated). Only 10- or 13-digit numbers are used. Books must be in the current list and eligible for DB sync."
        value={dbSyncBulkValue}
        onChange={setDbSyncBulkValue}
        parsedCount={dbSyncParsedCount}
        onSubmit={handleDbSyncBulkSubmit}
        submitButtonLabel="Sync to DB"
        statusLine={
          dbSyncParsedCount > 0
            ? `${dbSyncParsedCount} valid ISBN${dbSyncParsedCount !== 1 ? 's' : ''} — eligible books will be synced to admin tool (QA + PROD)`
            : 'No valid ISBNs yet'
        }
      />
    </>
  );
};

interface HeaderProps {
  currentView: 'info' | 'pages';
  onViewChange: (view: 'info' | 'pages') => void;
  onSearchChange: (query: string) => void;
  searchQuery: string;
  onBookFileSelected?: (bookId: string) => void;
  currentBookPath?: string;
  hasFilters?: boolean;
  isNotOnFirstPage?: boolean;
  onClearAllFilters?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  currentView,
  onViewChange,
  onSearchChange,
  searchQuery,
  onBookFileSelected,
  currentBookPath,
  hasFilters = false,
  isNotOnFirstPage = false,
  onClearAllFilters
}) => {
  const { userName, logout, accessLevel } = useAuth();
  const isKriyadocsUser = accessLevel === 'KRIYADOCS_USER';
  const isFullAccessUser = accessLevel === 'FULL_ACCESS';
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isS3Mode, setIsS3Mode] = useState(false);
  // For Sync(x) button only: show when local OR (server AND folder is books). Does not affect Kriyadocs-only (whole block hidden).
  const [folder, setFolder] = useState<string | null>(() => FolderService.getFolderIfSet());
  useEffect(() => {
    const handler = () => setFolder(FolderService.getFolderIfSet());
    window.addEventListener('folderChanged', handler);
    const initHandler = () => setFolder(FolderService.getFolderIfSet());
    window.addEventListener('app-initialization-complete', initHandler);
    return () => {
      window.removeEventListener('folderChanged', handler);
      window.removeEventListener('app-initialization-complete', initHandler);
    };
  }, []);
  const uploadAndSyncAvailable = !isS3Mode || folder === 'books';
  const [enlargeOnHover, setEnlargeOnHover] = useState(settingsService.getPagesTableEnlargeOnHover());
  const [dbExportStatus, setDbExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [dbExportMessage, setDbExportMessage] = useState<string>('');
  const [lastModifiedDate, setLastModifiedDate] = useState<string | null>(null);
  const [serverStatus, setServerStatus] = useState<{
    health: 'healthy' | 'warning' | 'critical';
    disk: { free: number; total: number; percentUsed: number };
    memory: { free: number; total: number; percentUsed: number };
    cpu: { usage: number };
    timestamp: string;
    allBooksManager?: {
      isRegenerating: boolean;
      regenProgress: { current: number; total: number } | null;
      regenError: string | null;
      pendingSaves: number;
      bookCount: number;
      lastRegenResult?: { booksCount: number; duration: number; completedAt: number } | null;
    };
  } | null>(null);
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);

  const handleLogout = () => {
    logout();
    // Reload the page to reset all state and show login screen
    window.location.reload();
  };

  const fetchLastModifiedDate = async () => {
    const folder = FolderService.getFolderIfSet();
    if (!folder) {
      setLastModifiedDate(null);
      return;
    }

    try {
      const folderHeaders = FolderService.getFolderHeader();
      const response = await authenticatedFetch('/api/db/lookup-data-last-modified', {
        headers: folderHeaders
      });
      const result = await response.json();
      
      if (result.status === 'ok' && result.lastModifiedFormatted) {
        setLastModifiedDate(result.lastModifiedFormatted);
      } else {
        setLastModifiedDate(null);
      }
    } catch (error) {
      setLastModifiedDate(null);
    }
  };

  const handleExportBookData = async () => {
    const folder = FolderService.getFolderIfSet();
    if (!folder) {
      setDbExportStatus('error');
      setDbExportMessage('No folder selected');
      return;
    }

    setDbExportStatus('loading');
    setDbExportMessage('Exporting book data...');

    try {
      const response = await authenticatedFetch('/api/db/export-book-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ folder }),
      });

      const result = await response.json();

      if (response.ok) {
        setDbExportStatus('success');
        setDbExportMessage(`Exported ${result.rowCount} books to ${result.savedTo}`);
        // Refresh last modified date after successful export
        await fetchLastModifiedDate();
        // Reset status after 5 seconds
        setTimeout(() => {
          setDbExportStatus('idle');
          setDbExportMessage('');
        }, 5000);
      } else {
        setDbExportStatus('error');
        setDbExportMessage(result.error || 'Export failed');
      }
    } catch (error: any) {
      setDbExportStatus('error');
      setDbExportMessage(error.message || 'Export failed');
    }
  };

  // Detect S3 mode - check base API endpoint instead
  useEffect(() => {
    const detectS3Mode = async () => {
      console.log('[Header] Detecting S3 mode...');
      try {
        const { authenticatedFetch } = await import('../utils/api');
        const response = await authenticatedFetch('/api/books/');
        if (response.ok) {
          const config = await response.json();
          const detectedMode = config.storageMode === 's3';
          console.log('[Header] S3 mode detected:', detectedMode, 'config:', config);
          setIsS3Mode(detectedMode);
          setFolder(FolderService.getFolderIfSet());
        } else {
          console.log('[Header] API response not ok:', response.status);
          setIsS3Mode(false);
        }
      } catch (error) {
        console.warn('[Header] Could not detect storage mode:', error);
        setIsS3Mode(false);
      }
    };

    detectS3Mode();
  }, []);

  // Sync enlarge on hover state when view changes
  useEffect(() => {
    if (currentView === 'pages') {
      setEnlargeOnHover(settingsService.getPagesTableEnlargeOnHover());
    }
  }, [currentView]);

  // Fetch server status
  const fetchServerStatus = async () => {
    try {
      const response = await authenticatedFetch('/api/server/server-status');
      if (response.ok) {
        const data = await response.json();
        setServerStatus(data);
      }
    } catch (error) {
      // Silently fail - don't show errors for health check
      console.warn('[Header] Failed to fetch server status:', error);
    }
  };

  // Poll server status - faster when regenerating
  useEffect(() => {
    // Fetch immediately on mount
    fetchServerStatus();
    
    // Poll every 30 seconds normally, every 500ms during regeneration
    const pollInterval = serverStatus?.allBooksManager?.isRegenerating ? 500 : 30000;
    
    const interval = setInterval(() => {
      fetchServerStatus();
    }, pollInterval);
    
    return () => clearInterval(interval);
  }, [serverStatus?.allBooksManager?.isRegenerating]);

  // Track previous lastRegenResult to detect when regeneration completes
  const prevLastRegenResultRef = React.useRef<typeof serverStatus.allBooksManager.lastRegenResult | null>(null);
  
  // Dispatch event when regeneration completes (lastRegenResult appears)
  useEffect(() => {
    const currentResult = serverStatus?.allBooksManager?.lastRegenResult;
    const prevResult = prevLastRegenResultRef.current;
    
    // If we just got a result (transition from null to value)
    if (currentResult && !prevResult) {
      console.log('[Header] Regeneration completed, dispatching bookListRegenerated event');
      window.dispatchEvent(new CustomEvent('bookListRegenerated'));
    }
    
    prevLastRegenResultRef.current = currentResult;
  }, [serverStatus?.allBooksManager?.lastRegenResult]);

  // Fetch last modified date when folder changes
  useEffect(() => {
    fetchLastModifiedDate();
    
    // Listen for folder changes
    const handleFolderChange = () => {
      fetchLastModifiedDate();
    };
    
    window.addEventListener('folderChanged', handleFolderChange);
    return () => {
      window.removeEventListener('folderChanged', handleFolderChange);
    };
  }, []);

  // Handle regeneration trigger
  const handleRegenerateAllBooks = async () => {
    setRegenConfirmOpen(false);
    
    const folder = FolderService.getFolderIfSet();
    if (!folder) {
      console.error('[Header] No folder selected for regeneration');
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/books/all-books-manager/regenerate?folder=${encodeURIComponent(folder)}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('[Header] Regeneration failed:', data.error);
        // Could show a toast notification here
        return;
      }
      
      console.log('[Header] Regeneration started successfully');
      // Fetch status immediately to update UI
      fetchServerStatus();
    } catch (error) {
      console.error('[Header] Error starting regeneration:', error);
    }
  };

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Get health icon and tooltip text
  const getHealthInfo = () => {
    if (!serverStatus) {
      return {
        icon: '⚪',
        color: '#9ca3af',
        tooltip: 'Server health: Unknown'
      };
    }

    const { health, disk, memory, cpu } = serverStatus;
    const diskFree = formatBytes(disk.free);
    const diskTotal = formatBytes(disk.total);
    const memoryFree = formatBytes(memory.free);
    const memoryTotal = formatBytes(memory.total);
    
    const tooltip = `Server Health: ${health.toUpperCase()}\n\n` +
      `Disk: ${diskFree} free / ${diskTotal} total (${disk.percentUsed.toFixed(1)}% used)\n` +
      `Memory: ${memoryFree} free / ${memoryTotal} total (${memory.percentUsed.toFixed(1)}% used)\n` +
      `CPU: ${cpu.usage.toFixed(1)}% usage\n\n` +
      `Last updated: ${new Date(serverStatus.timestamp).toLocaleTimeString()}`;

    if (health === 'healthy') {
      return { icon: '🟢', color: '#22c55e', tooltip };
    } else if (health === 'warning') {
      return { icon: '🟡', color: '#eab308', tooltip };
    } else {
      return { icon: '🔴', color: '#ef4444', tooltip };
    }
  };

  return (
    <>
      {/* All-Books Regeneration Status Banner */}
      {serverStatus?.allBooksManager?.isRegenerating && (
        <div className="all-books-regen-banner">
          <div className="all-books-regen-banner-content">
            <span className="all-books-regen-spinner">🔄</span>
            <span>
              Regenerating book list...
              {serverStatus.allBooksManager.regenProgress && (
                <> {serverStatus.allBooksManager.regenProgress.current}/{serverStatus.allBooksManager.regenProgress.total}</>
              )}
              {serverStatus.allBooksManager.pendingSaves > 0 && (
                <> ({serverStatus.allBooksManager.pendingSaves} saves pending)</>
              )}
            </span>
          </div>
          <div className="all-books-regen-progress">
            {serverStatus.allBooksManager.regenProgress && (
              <div 
                className="all-books-regen-progress-bar"
                style={{ 
                  width: `${(serverStatus.allBooksManager.regenProgress.current / serverStatus.allBooksManager.regenProgress.total) * 100}%` 
                }}
              />
            )}
          </div>
        </div>
      )}
      
      {/* Regeneration Error Banner */}
      {serverStatus?.allBooksManager?.regenError && !serverStatus?.allBooksManager?.isRegenerating && (
        <div className="all-books-regen-banner all-books-regen-banner-error">
          <div className="all-books-regen-banner-content">
            <span>⚠️</span>
            <span>Regeneration failed: {serverStatus.allBooksManager.regenError}</span>
            <button 
              onClick={() => setRegenConfirmOpen(true)}
              className="all-books-regen-retry-button"
            >
              Retry
            </button>
          </div>
        </div>
      )}
      
      {/* Regeneration Success Banner */}
      {serverStatus?.allBooksManager?.lastRegenResult && !serverStatus?.allBooksManager?.isRegenerating && !serverStatus?.allBooksManager?.regenError && (
        <div className="all-books-regen-banner all-books-regen-banner-success">
          <div className="all-books-regen-banner-content">
            <span>✅</span>
            <span>
              Book list rebuilt: {serverStatus.allBooksManager.lastRegenResult.booksCount} books 
              in {(serverStatus.allBooksManager.lastRegenResult.duration / 1000).toFixed(1)}s
            </span>
          </div>
        </div>
      )}

      <header className="app-header-compact">
      <div className="header-content-compact header-nav-content">
        {/* Far Left - Title */}
        <div className="header-nav-left">
          <h1 className="app-title-compact header-nav-title">Book Admin Tool</h1>
          {/* Info | Pages buttons - below title */}
          <div className="header-nav-view-buttons">
            <div className="header-nav-sync-container">
              <div className="header-nav-icon-container">
                <button
                  onClick={() => onViewChange('info')}
                  className={`header-nav-button ${currentView === 'info' ? 'active' : ''}`}
                >
                  <span>📊</span>
                  <span>Info</span>
                </button>
              </div>
              <div className="header-nav-icon-container">
                <button
                  onClick={() => onViewChange('pages')}
                  className={`header-nav-button ${currentView === 'pages' ? 'active' : ''}`}
                >
                  <span>📄</span>
                  <span>Pages</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Middle Section - Sync Controls */}
        <div className="header-nav-middle">
          {/* Sync Controls Container - only for full-access users */}
          {isFullAccessUser && (
            <div className="header-nav-sync-container">
              {/* Group 1: Upload Books */}
              <div className="header-nav-group">
                <div className="header-nav-group-title">Upload Books</div>
                <div className="header-nav-group-content">
                  {/* Image Ready Filter Button */}
                  <div className="header-nav-icon-container">
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('applyFilterPreset', {
                          detail: {
                            filters: {
                              images_created: ['true'],
                              text_extracted: [''],
                              approval_images: ['Approved'],
                              production_file_sync_status: ['not_synced']
                            }
                          }
                        }));
                      }}
                      className="header-nav-button header-nav-button-icon-only"
                      title="A filter to identify books that are ready to upload - images reviewed and no text available"
                    >
                      <span>🖼️</span>
                      <span>Ready</span>
                    </button>
                  </div>
                  {/* Words Ready Filter Button */}
                  <div className="header-nav-icon-container">
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('applyFilterPreset', {
                          detail: {
                            filters: {
                              images_created: ['true'],
                              text_extracted: ['enhanced'],
                              approval_images: ['Approved'],
                              approval_words: ['Approved'],
                              production_file_sync_status: ['not_synced']
                            }
                          }
                        }));
                      }}
                      className="header-nav-button header-nav-button-icon-only"
                      title="A filter to identify books that are ready to upload - images and text reviewed"
                    >
                      <span>📝</span>
                      <span>Ready</span>
                    </button>
                  </div>
                  {/* Sync Selected Files Button - only when local OR (server AND folder is books) */}
                  {uploadAndSyncAvailable && (
                    <div className="header-nav-icon-container">
                      <SyncSelectedFilesButton />
                    </div>
                  )}
                  {/* DB Sync Selected Button - same visibility as Upload Files */}
                  {uploadAndSyncAvailable && (
                    <div className="header-nav-icon-container">
                      <DbSyncSelectedButton />
                    </div>
                  )}
                </div>
              </div>

              {/* Group 2: Statuses */}
              <div className="header-nav-group">
                <div className="header-nav-group-title">Statuses</div>
                <div className="header-nav-group-content">
                  {/* File Sync Status Icon */}
                  <div className="header-nav-icon-container header-nav-icon-container-square">
                    <FileSyncStatusIcon />
                  </div>
                  {/* Save Queue Status Icon */}
                  <div className="header-nav-icon-container header-nav-icon-container-square">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        cursor: 'pointer',
                        fontSize: '20px',
                        position: 'relative',
                        transition: 'transform 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      className="save-queue-status-icon"
                    >
                      <span style={{ 
                        color: serverStatus?.allBooksManager?.isRegenerating 
                          ? '#f59e0b' 
                          : (serverStatus?.allBooksManager?.pendingSaves || 0) > 0 
                            ? '#3b82f6' 
                            : '#9ca3af',
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                      }}>
                        💾
                      </span>
                      {(serverStatus?.allBooksManager?.pendingSaves || 0) > 0 && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            backgroundColor: serverStatus?.allBooksManager?.isRegenerating ? '#f59e0b' : '#3b82f6',
                            color: 'white',
                            borderRadius: '50%',
                            minWidth: '26px',
                            minHeight: '26px',
                            padding: '0 6px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            border: '2px solid var(--bg-primary)',
                            boxSizing: 'border-box',
                          }}
                        >
                          {serverStatus?.allBooksManager?.pendingSaves}
                        </span>
                      )}
                      <div className="help-tooltip" style={{ minWidth: '280px', maxWidth: '400px' }}>
                        <div className="tooltip-content">
                          <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                            Save Queue Status
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', tableLayout: 'fixed' }}>
                            <colgroup>
                              <col style={{ width: '55%' }} />
                              <col style={{ width: '45%' }} />
                            </colgroup>
                            <tbody>
                              <tr>
                                <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Pending Saves:</td>
                                <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                  {serverStatus?.allBooksManager?.pendingSaves ?? 0}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Total Books:</td>
                                <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                  {serverStatus?.allBooksManager?.bookCount ?? 0}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Status:</td>
                                <td style={{ 
                                  padding: '0.25rem 0 0.25rem 0.5rem', 
                                  fontWeight: 500, 
                                  whiteSpace: 'nowrap',
                                  color: serverStatus?.allBooksManager?.isRegenerating 
                                    ? '#f59e0b' 
                                    : serverStatus?.allBooksManager?.regenError 
                                      ? '#ef4444'
                                      : 'var(--accent-success)'
                                }}>
                                  {serverStatus?.allBooksManager?.isRegenerating 
                                    ? 'Regenerating...' 
                                    : serverStatus?.allBooksManager?.regenError 
                                      ? 'Error'
                                      : 'Ready'}
                                </td>
                              </tr>
                              {serverStatus?.allBooksManager?.isRegenerating && serverStatus.allBooksManager.regenProgress && (
                                <tr>
                                  <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Progress:</td>
                                  <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: '#f59e0b', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    {serverStatus.allBooksManager.regenProgress.current}/{serverStatus.allBooksManager.regenProgress.total}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Server Health Icon */}
                  <div className="header-nav-server-health server-health-icon">
                    {getHealthInfo().icon}
                    {serverStatus && (
                      <div className="help-tooltip" style={{ minWidth: '280px', width: '450px' }}>
                        <div className="tooltip-content">
                          <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                            Server Health: {serverStatus.health.toUpperCase()}
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', tableLayout: 'fixed' }}>
                            <colgroup>
                              <col style={{ width: '25%' }} />
                              <col style={{ width: '50%' }} />
                              <col style={{ width: '25%' }} />
                            </colgroup>
                            <tbody>
                              <tr>
                                <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Disk:</td>
                                <td style={{ padding: '0.25rem 0.5rem', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                                  {formatBytes(serverStatus.disk.free)} free / {formatBytes(serverStatus.disk.total)} total
                                </td>
                                <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  ({serverStatus.disk.percentUsed.toFixed(1)}%)
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Memory:</td>
                                <td style={{ padding: '0.25rem 0.5rem', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                                  {formatBytes(serverStatus.memory.free)} free / {formatBytes(serverStatus.memory.total)} total
                                </td>
                                <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  ({serverStatus.memory.percentUsed.toFixed(1)}%)
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>CPU:</td>
                                <td style={{ padding: '0.25rem 0.5rem', color: 'var(--text-primary)', whiteSpace: 'nowrap' }} colSpan={2}>
                                  {serverStatus.cpu.usage.toFixed(1)}% usage
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                            Updated: {new Date(serverStatus.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Group 3: Recreate Lists on Server */}
              <div className="header-nav-group">
                <div className="header-nav-group-title">Recreate Lists on Server</div>
                <div className="header-nav-group-content">
                  {/* Regenerate Book List Button */}
                  <div className="header-nav-icon-container">
                    <button
                      onClick={() => setRegenConfirmOpen(true)}
                      disabled={serverStatus?.allBooksManager?.isRegenerating}
                      className="header-nav-button header-nav-button-icon-only"
                      title={
                        serverStatus?.allBooksManager?.isRegenerating
                          ? `Regenerating... ${serverStatus.allBooksManager.regenProgress 
                              ? `${serverStatus.allBooksManager.regenProgress.current}/${serverStatus.allBooksManager.regenProgress.total}` 
                              : ''}`
                          : 'Regenerate book list from spine files'
                      }
                      style={{
                        opacity: serverStatus?.allBooksManager?.isRegenerating ? 0.6 : 1,
                        cursor: serverStatus?.allBooksManager?.isRegenerating ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span style={{ 
                        animation: serverStatus?.allBooksManager?.isRegenerating ? 'spin 1s linear infinite' : 'none' 
                      }}>🔄</span>
                      <span>All Books List</span>
                    </button>
                  </div>
                  {/* Database Button */}
                  <div className="header-nav-icon-container">
                    <button
                      onClick={handleExportBookData}
                      className={`header-nav-db-button ${dbExportStatus !== 'idle' ? 'header-nav-db-button-status' : ''}`}
                      title={
                        dbExportMessage || 
                        (lastModifiedDate 
                          ? `Refresh Book data from database - last checked ${lastModifiedDate}`
                          : 'Refresh Book data from database')
                      }
                      style={{
                        color: dbExportStatus === 'error' ? '#ef4444' : dbExportStatus === 'success' ? '#22c55e' : dbExportStatus === 'loading' ? 'var(--accent-primary)' : undefined
                      }}
                    >
                      <span>⛁</span>
                      <span>Database</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Enlarge on hover toggle (only for pages view) */}
          {currentView === 'pages' && (
            <div className="header-nav-enlarge-container">
              <label className="header-nav-enlarge-label">
                <span>Enlarge on hover</span>
                <div
                  className={`header-nav-enlarge-toggle ${enlargeOnHover ? 'active' : ''}`}
                  onClick={() => {
                    const newValue = !enlargeOnHover;
                    setEnlargeOnHover(newValue);
                    settingsService.setPagesTableEnlargeOnHover(newValue);
                    window.dispatchEvent(new CustomEvent('pagesTableEnlargeToggle', { detail: { enabled: newValue } }));
                  }}
                >
                  <div className="header-nav-enlarge-toggle-slider" />
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Right Section - Search, Folder, Name/Logout */}
        <div className="header-nav-right">
          {/* Search Box with Clear All below */}
          <div className="header-nav-search-wrapper">
            <div className={`search-container-compact ${isSearchFocused ? 'focused' : ''}`}>
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search books..."
                className="search-input-compact"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => onSearchChange('')}
                  aria-label="Clear search"
                >
                  <span className="search-clear-icon">✕</span>
                </button>
              )}
            </div>
            {(searchQuery || hasFilters || isNotOnFirstPage) && onClearAllFilters && (
              <button
                onClick={onClearAllFilters}
                className="header-nav-clear-all"
                aria-label="Clear all filters and search"
              >
                <span>🗑️</span>
                <span>Clear All</span>
              </button>
            )}
          </div>

          {/* Folder Selector with lists below */}
          {onBookFileSelected && (
            <div className="header-nav-folder-wrapper">
              <BookFileSelector 
                onBookFileSelected={onBookFileSelected}
                currentBookId={currentBookPath}
                isS3Mode={isS3Mode}
              />
            </div>
          )}

          {/* Name and Logout */}
          {userName && (
            <div className="header-nav-user-container">
              <button
                onClick={handleLogout}
                className="header-nav-logout"
              >
                Logout
              </button>
              <span className="header-nav-user-name">
                <span>{userName}</span>
                {isKriyadocsUser && (
                  <span className="header-nav-user-kriyadocs">
                    (Kriyadocs)
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </header>

      {/* Regeneration Confirmation Dialog */}
      {regenConfirmOpen && (
        <div className="modal-overlay" onClick={() => setRegenConfirmOpen(false)}>
          <div className="modal-content all-books-regen-confirm" onClick={e => e.stopPropagation()}>
            <h3>Regenerate Book List?</h3>
            <p>
              This will rebuild all-books.json from all spine files. 
              This may take a few minutes depending on the number of books.
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Saves made during regeneration will be queued and applied when complete.
            </p>
            <div className="modal-actions">
              <button 
                onClick={() => setRegenConfirmOpen(false)}
                className="modal-button modal-button-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={handleRegenerateAllBooks}
                className="modal-button modal-button-primary"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

Header.displayName = 'Header';

// Memoize component to prevent unnecessary re-renders
export default React.memo(Header, (prevProps, nextProps) => {
  // Only re-render if these props actually change
  return (
    prevProps.currentView === nextProps.currentView &&
    prevProps.searchQuery === nextProps.searchQuery &&
    prevProps.currentBookPath === nextProps.currentBookPath &&
    prevProps.hasFilters === nextProps.hasFilters &&
    prevProps.isNotOnFirstPage === nextProps.isNotOnFirstPage &&
    prevProps.onViewChange === nextProps.onViewChange &&
    prevProps.onSearchChange === nextProps.onSearchChange &&
    prevProps.onBookFileSelected === nextProps.onBookFileSelected &&
    prevProps.onClearAllFilters === nextProps.onClearAllFilters
  );
});
